// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { IFormVendor, IPayloadEditVendor, IQueryParamDetailVendor } from '~/utils/interface/vendors';
import { defaultValuesFormVendor, schema } from './helper';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { EMPTY_STRING, optionVendorType } from '~/utils/constants/common';
import { formatPhoneNumber, removeEmptyObjects, removeNonDigits } from '~/utils/helper/common';
import { createVendor, editVendor, getDetailVendor } from '~/thunks/vendors/vendorsThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeVendorDetailEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './VendorForm.module.scss';
import { icons } from '~/assets';

type Props = {
  vendorId?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const VendorFormModal = (props: Props) => {
  //#region Destructuring Props
  const { onClose, vendorId } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/vendors']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormVendor>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesFormVendor,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!vendorId) return;
    const params: IQueryParamDetailVendor = {
      vendorId: vendorId,
      type: TypeVendorDetailEnum.OVERVIEW,
    };
    handleGetDetailVendor(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose();
    reset(defaultValuesFormVendor);
  };

  const handleSubmitForm = (data: IFormVendor) => {
    if (vendorId) {
      handleEditVendor(data);
    } else {
      handleCreateVendor(data);
    }
  };

  const handleCreateVendor = (data: IFormVendor) => {
    const newData: IFormVendor = {
      ...data,
      phoneNumber: removeNonDigits(data.phoneNumber),
    };

    const body = removeEmptyObjects(newData);
    setIsLoading(true);
    dispatch(createVendor(body))
      .unwrap()
      .then((res) => {
        handleCloseModal();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditVendor = (data: IFormVendor) => {
    if (!vendorId) return;

    const newData: IFormVendor = {
      ...data,
      phoneNumber: removeNonDigits(data.phoneNumber),
    };

    const payload: IPayloadEditVendor = {
      vendorId: vendorId,
      body: newData,
    };

    setIsLoading(true);
    dispatch(editVendor(payload))
      .unwrap()
      .then((res) => {
        handleCloseModal();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleGetDetailVendor = (params: IQueryParamDetailVendor) => {
    setIsLoading(true);
    dispatch(getDetailVendor(params))
      .unwrap()
      .then((res) => {
        const formatData: IFormVendor = {
          vendorName: res.data?.vendorName || EMPTY_STRING,
          email: res.data?.email || EMPTY_STRING,
          address: res.data?.address || EMPTY_STRING,
          city: res.data?.city || EMPTY_STRING,
          contactName: res.data?.contactName || EMPTY_STRING,
          type: res.data?.type || EMPTY_STRING,
          website: res.data?.website || EMPTY_STRING,
          postalCode: res.data?.postalCode || EMPTY_STRING,
          state: res.data?.state || EMPTY_STRING,
          phoneNumber: res.data?.phoneNumber ? formatPhoneNumber(res.data?.phoneNumber) : EMPTY_STRING,
          description: res.data?.description || EMPTY_STRING,
        };
        reset(formatData);
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const fetchOptionsVendorType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionVendorType,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='vendorFormModal'
      aria-labelledby='vendorFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='truckFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={vendorId ? t('form_edit_vendor_title') : t('form_add_vendor_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2
          columnSpacing={'24px'}
          rowSpacing={'24px'}
          container
          sx={{
            width: '100%',
          }}
        >
          <Grid2 size={6}>
            <Controller
              name='vendorName'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_vendor_name_label')}
                  placeholder={t('form_vendor_vendor_name_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.vendorName?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='type'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  gap={10}
                  height={36}
                  styleType='contained'
                  value={value}
                  onChange={(data) => onChange(data?.value)}
                  label={t('form_vendor_vendor_type_label')}
                  placeholder={t('common_text_placeholder_select', {
                    ns: 'common/base',
                  })}
                  callApiFn={fetchOptionsVendorType}
                  errorMessage={errors.type?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='contactName'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_contact_name_label')}
                  placeholder={t('form_vendor_contact_name_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.contactName?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='email'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_email_label')}
                  placeholder={t('form_vendor_email_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.email?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='website'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_website_label')}
                  placeholder={t('form_vendor_website_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.website?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='address'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_address_label')}
                  placeholder={t('form_vendor_address_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.address?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='postalCode'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_zipcode_label')}
                  placeholder={t('form_vendor_zipcode_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.postalCode?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='city'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_city_label')}
                  placeholder={t('form_vendor_city_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.city?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='state'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_state_label')}
                  placeholder={t('form_vendor_state_placeholder')}
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.state?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='phoneNumber'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_vendor_phone_number_label')}
                  placeholder={t('form_vendor_phone_number_placeholder')}
                  height={36}
                  value={value}
                  onChange={(event) => {
                    const formattedValue = formatPhoneNumber(event.target.value);
                    onChange(formattedValue);
                  }}
                  errorMessage={errors.phoneNumber?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              name='description'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  multiline
                  rows={7}
                  height={'auto'}
                  label={t('form_vendor_description_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.description?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          disabled={vendorId ? !isDirty : false}
          onClick={handleSubmit(handleSubmitForm)}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default VendorFormModal;
